import { useEffect } from "react";
import { Typography } from "@mui/material";
import { useAsync } from "react-use";
import i18n from "../../../../../i18n";
import substitutePhoto from "../../../../images/profile_substitute.svg";
import { getIdPhotoSrc } from "../hooks/proctoringResultViewHelper";
import styles from "./IdPhotosPanel.module.css";

type IdPhotosPanelProps = {
	sessionUuid: string;
	handleImgError: (ev: React.SyntheticEvent<HTMLImageElement, Event>) => void;
};

/**
 * Fetches on mount rather than from the parent hook so the two photos are still only downloaded when the
 * tab is open — the panel is mounted conditionally, and `ApiClient.fetch` serialises every request behind
 * one promise, so loading them eagerly would sit in front of the rest of the page.
 */
function useIdPhoto(sessionUuid: string, type: "id" | "person") {
	const { value, loading } = useAsync(() => getIdPhotoSrc(sessionUuid, type), [sessionUuid, type]);

	useEffect(() => {
		return () => {
			if (value) URL.revokeObjectURL(value);
		};
	}, [value]);

	if (loading) return undefined;
	return value ?? substitutePhoto;
}

export default function IdPhotosPanel({ sessionUuid, handleImgError }: IdPhotosPanelProps) {
	const idPhotoSrc = useIdPhoto(sessionUuid, "id");
	const personPhotoSrc = useIdPhoto(sessionUuid, "person");

	return (
		<div className={styles.idPhotoPanel}>
			<div className={styles.idPhotoContainer}>
				<Typography variant="body1" fontWeight={600} color={"#FFFFFF"}>
					{i18n.t("result.components.resultsPage.proctoringResultsViewTSX.idPhoto")}
				</Typography>
				{idPhotoSrc && (
					<img
						src={idPhotoSrc}
						alt={i18n.t("result.components.resultsPage.proctoringResultsViewTSX.idPhotoAlt")}
						onError={handleImgError}
						className={styles.idPhotoImage}
					/>
				)}
			</div>
			<div className={styles.idPhotoContainer}>
				<Typography variant="body1" fontWeight={600} color={"#FFFFFF"}>
					{i18n.t("result.components.resultsPage.proctoringResultsViewTSX.verificationPhoto")}
				</Typography>
				{personPhotoSrc && (
					<img
						src={personPhotoSrc}
						alt={i18n.t("result.components.resultsPage.proctoringResultsViewTSX.verificationPhotoAlt")}
						onError={handleImgError}
						className={styles.idPhotoImage}
					/>
				)}
			</div>
		</div>
	);
}
